/**
 * Accès : connexion (RG-LOG-001/002/003), mot de passe oublié (RG-LOG-010),
 * réinitialisation par lien, ouverture d'une session GodView (RG-LOG-020).
 */
import { type FormEvent, useEffect, useState } from 'react';
import { api, type ContratAChoisir, type Theme } from '../api';
import { Champ, Erreur, appliquerTheme, jjmmaaaa, messageErreur, naviguer } from '../commun';

const CLE_IDENTIFIANT = 'playtime.identifiant'; // « se souvenir de moi » : identifiant uniquement (§16)

export function Connexion({ surConnexion }: { surConnexion: () => void }): JSX.Element {
  const [login, setLogin] = useState(() => localStorage.getItem(CLE_IDENTIFIANT) ?? '');
  const [seSouvenir, setSeSouvenir] = useState(() => localStorage.getItem(CLE_IDENTIFIANT) !== null);
  const [motDePasse, setMotDePasse] = useState('');
  const [contrats, setContrats] = useState<ContratAChoisir[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tentatives, setTentatives] = useState(0); // RG-LOG-001 : 3 tentatives
  const [enCours, setEnCours] = useState(false);
  const [entete, setEntete] = useState<{ texte: string; operateur: string; version: string } | null>(null);
  const [contratChoisi, setContratChoisi] = useState<number | null>(null);
  const [oublie, setOublie] = useState(false);

  useEffect(() => { api.disclaimer().then(setEntete).catch(() => undefined); }, []);
  // Lot 8 : thème et image de fond de l'opérateur choisi — ?op=CODE dans l'adresse, sinon le dernier choix (mémorisé
  // dans le navigateur), sinon le premier opérateur qui possède des images. Le choix ne change que l'apparence :
  // la connexion elle-même retrouve l'opérateur par l'identifiant.
  const [theme, setTheme] = useState<Theme | null>(null);
  const [fond, setFond] = useState<string | null>(null);
  const [operateurs, setOperateurs] = useState<{ code: string; nom: string; images: number; logo: string | null }[]>([]);
  const [op, setOp] = useState<string | null>(null);
  useEffect(() => {
    const demande = new URLSearchParams(window.location.search).get('op') ?? new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('op');
    api.operateursPublics().then((liste) => {
      setOperateurs(liste);
      let memorise: string | null = null;
      try { memorise = window.localStorage.getItem('playtime.op'); } catch { /* stockage indisponible */ }
      const choix = [demande, memorise].find((c) => c && liste.some((o) => o.code === c)) ?? liste.find((o) => o.images > 0)?.code ?? liste[0]?.code ?? null;
      // (« Playtime », l'aspect d'origine, est en tête de liste ; il est choisi d'office s'il a des images.)
      setOp(choix);
    }).catch(() => setOp(demande));
  }, []);
  useEffect(() => {
    if (op === null) return;
    api.theme(op).then((t) => { setTheme(t); appliquerTheme(t.style); setFond(t.images > 0 ? `/api/v1/auth/fond?op=${encodeURIComponent(t.code)}&r=${Date.now()}` : null); }).catch(() => undefined);
    try { window.localStorage.setItem('playtime.op', op); window.sessionStorage.setItem('playtime.theme.session', op); } catch { /* ignoré */ }
    const url = new URL(window.location.href); url.searchParams.set('op', op); window.history.replaceState(null, '', url.toString());
  }, [op]);
  const autreImage = () => { if (theme && theme.images > 1) setFond(`/api/v1/auth/fond?op=${encodeURIComponent(theme.code)}&r=${Date.now()}`); };

  async function tenter(idContrat?: number): Promise<void> {
    setErreur(null); setEnCours(true);
    try {
      const r = await api.login(login, motDePasse, idContrat);
      if (seSouvenir) localStorage.setItem(CLE_IDENTIFIANT, login); else localStorage.removeItem(CLE_IDENTIFIANT);
      if (r.type === 'selection-contrat') setContrats(r.contrats); else surConnexion();
    } catch (e) {
      setTentatives((t) => t + 1);
      setErreur(messageErreur(e));
      if (idContrat !== undefined) setContrats(null);
    } finally { setEnCours(false); }
  }

  const bloque = tentatives >= 3;

  return (
    <div className="scene-connexion" style={fond ? { backgroundImage: `url("${fond}")` } : undefined}>
      <header className="barre-principale barre-connexion">
        <div className="marque-operateur">{theme?.logo ? <img className="logo-image" src={theme.logo} alt="" /> : <span className="logo-operateur">{theme?.style.sigle || 'P.'}</span>}<span className="nom-operateur"><strong>{theme?.nom ?? entete?.operateur ?? 'Playtime'}</strong></span></div>
        {operateurs.length > 1 && (
          <div className="choix-operateur">
            <label>Thème <select value={op ?? ''} onChange={(e) => setOp(e.target.value)}>{operateurs.map((o) => <option key={o.code} value={o.code}>{o.nom}{o.images ? ` (${o.images} images)` : ''}</option>)}</select></label>
            {theme && theme.images > 1 && <button type="button" className="discret" onClick={autreImage} title="Autre image de fond">↻</button>}
          </div>
        )}
        <h1 className="titre-application">GESTION DES PLANNINGS ET DES PRESTATIONS</h1>
        <span className="version">{entete ? `v ${entete.version}` : ''}</span>
      </header>
      <div className="carte-connexion">

        {oublie ? (
          <MotDePasseOublie loginInitial={login} surRetour={() => setOublie(false)} />
        ) : contrats === null ? (
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (!bloque) void tenter(); }}>
            <Champ libelle="Nom :" enfants={<input autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} required disabled={bloque} />} />
            <Champ libelle="Mot de passe :" enfants={<input type="password" autoComplete="current-password" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} required disabled={bloque} />} />
            <label className="case"><input type="checkbox" checked={seSouvenir} onChange={(e) => setSeSouvenir(e.target.checked)} /> Se souvenir de moi</label>
            <Erreur message={bloque ? 'Trois tentatives infructueuses : rechargez la page pour réessayer.' : erreur} />
            <div className="pied-connexion">
              <button type="button" className="lien" onClick={() => setOublie(true)}>J'ai oublié mon mot de passe</button>
              <button className="primaire" disabled={enCours || bloque}>Se connecter</button>
            </div>
          </form>
        ) : (
          <div className="selection-contrat" role="dialog" aria-label="Sélectionnez le contrat pour lequel vous souhaitez vous connecter">
            <h2>Sélectionnez le contrat pour lequel vous souhaitez vous connecter</h2>
            <p>Le compte sélectionné a plusieurs contrats, veuillez préciser celui pour lequel vous souhaitez vous connecter :</p>
            <select value={contratChoisi ?? contrats[0]?.idContrat} onChange={(e) => setContratChoisi(Number(e.target.value))}>
              {contrats.map((c) => <option key={c.idContrat} value={c.idContrat}>contrat du {jjmmaaaa(c.premierJourContrat)} au {c.dernierJourContrat ? jjmmaaaa(c.dernierJourContrat) : '31/12/2099'}</option>)}
            </select>
            <Erreur message={erreur} />
            <div className="pied-connexion"><span /><button className="primaire" disabled={enCours} onClick={() => void tenter(contratChoisi ?? contrats[0]?.idContrat)}>OK</button></div>
          </div>
        )}
      </div>
      {entete && <footer className="disclaimer">{entete.texte}</footer>}
    </div>
  );
}

function MotDePasseOublie({ loginInitial, surRetour }: { loginInitial: string; surRetour: () => void }): JSX.Element {
  const [login, setLogin] = useState(loginInitial);
  const [message, setMessage] = useState<string | null>(null);
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); void api.demandeMotDePasse(login).then((r) => setMessage(r.message)).catch((err) => setMessage(messageErreur(err))); }}>
      <Champ libelle="Identifiant" enfants={<input value={login} onChange={(e) => setLogin(e.target.value)} required />} aide="Un lien valable une heure sera envoyé à l'adresse associée." />
      {message && <p className="info">{message}</p>}
      <button className="primaire pleine-largeur">Envoyer le lien</button>
      <button type="button" className="discret pleine-largeur" onClick={surRetour}>Retour</button>
    </form>
  );
}

/** Page `#/reinitialisation?token=` (lien M7). */
export function Reinitialisation({ token }: { token: string }): JSX.Element {
  const [mdp, setMdp] = useState(''); const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null); const [fait, setFait] = useState(false);
  return (
    <div className="scene-connexion"><div className="carte-connexion">
      <h1 className="marque">Playtime<span className="point">.</span></h1>
      {fait ? (
        <><p className="info">Mot de passe modifié. Vous pouvez vous connecter.</p><button className="primaire pleine-largeur" onClick={() => naviguer('connexion')}>Se connecter</button></>
      ) : (
        <form onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (mdp !== confirmation) { setErreur('Les deux saisies diffèrent'); return; }
          void api.reinitialiserMotDePasse(token, mdp).then(() => setFait(true)).catch((err) => setErreur(messageErreur(err)));
        }}>
          <Champ libelle="Nouveau mot de passe" enfants={<input type="password" autoComplete="new-password" value={mdp} onChange={(e) => setMdp(e.target.value)} minLength={12} required />} aide="12 caractères minimum." />
          <Champ libelle="Confirmation" enfants={<input type="password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required />} />
          <Erreur message={erreur} />
          <button className="primaire pleine-largeur">Enregistrer</button>
        </form>
      )}
    </div></div>
  );
}

/** Page `#/godview?guid=` : consomme le jeton puis recharge la session. */
export function OuvertureGodView({ guid, surSession }: { guid: string; surSession: () => void }): JSX.Element {
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => { api.ouvrirGodView(guid).then(() => { naviguer('tableau-de-bord'); surSession(); }).catch((e) => setErreur(messageErreur(e))); }, [guid, surSession]);
  return <div className="scene-connexion"><div className="carte-connexion"><h1 className="marque">Playtime<span className="point">.</span></h1>{erreur ? <Erreur message={erreur} /> : <p className="info">Ouverture de la session « connecté en tant que »…</p>}</div></div>;
}
